import React, { useState, useRef } from 'react';
import './ImageUploader.css'; // We can reuse some styles or create new ones
import useFileDrop from '../hooks/useFileDrop';

const AudioUploader = ({ locationId, fieldType, onAudioUploaded, existingAudioUrl, questId, disabled }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const uploadFiles = async (files) => {
    if (!files.length || isUploading || disabled) return;

    // Validate every file up front.
    const allowed = ['audio/wav', 'audio/ogg', 'audio/mpeg'];
    for (const f of files) {
      if (!allowed.includes(f.type)) {
        setUploadError('Please select WAV, OGG, or MP3 audio files.');
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        setUploadError(`"${f.name}" is larger than 10MB.`);
        return;
      }
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Upload sequentially; each file becomes its own new version.
      let last = null;
      for (const file of files) {
        const formData = new FormData();
        formData.append('audio', file); // field name must match server-side multer setup
        formData.append('locationId', locationId);
        formData.append('fieldType', fieldType);
        formData.append('questId', questId);
        // Even though server blocks writes to prod, we send 'quests_test' to be explicit.
        formData.append('table', 'quests_test');

        const response = await fetch('/api/upload-audio', {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to upload "${file.name}"`);
        }

        last = await response.json();
      }

      if (onAudioUploaded && last) {
        onAudioUploaded(last.audioUrl, last.originalSize, last.compressedSize, last.originalMimeType);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Audio upload error:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => uploadFiles(Array.from(e.target.files || []));

  const { isDragOver, dropProps } = useFileDrop(uploadFiles, { disabled: disabled || isUploading });

  return (
    <div className={isDragOver ? 'audio-uploader is-drop-target' : 'audio-uploader'} {...dropProps}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="audio/wav,audio/ogg,audio/mpeg,.mp3"
        multiple
        className="file-input" // Shared class with ImageUploader
      />
      
      <button 
        className="upload-button" // Shared class
        onClick={handleUploadClick}
        disabled={isUploading || disabled}
        title="Pick files or drop them onto this button"
      >
        {isUploading ? 'Uploading...' : 'Add Voiceover'}
      </button>
      
      {uploadError && (
        <div className="upload-error">{uploadError}</div> // Shared class
      )}
    </div>
  );
};

export default AudioUploader; 